import { DialogContent, CircularProgress, Typography, Button, Box, useTheme } from '@mui/material';
import { CustomDialogContainer, CustomDialogTitle } from 'apps/t2c-fe/Common/dialog';
import { Styles } from '@t2c/shared-fe';
import { alpha } from '@mui/material/styles';

type RenderGateMode = 'processing' | 'upgrade' | 'noPlan';

interface RenderGateModalProps {
  open: boolean;
  mode: RenderGateMode;
  onClose: () => void;
  userPlanData: any;
  agentPlanData: any;
  push: any;
}

const styles: Styles = {
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  title: {
    color: (theme) => theme.palette.text.primary,
    paddingBottom: '10px',
    paddingTop: '10px',
    textAlign: 'center',
  },
  dialogContent: {
    padding: '16px',
    background: (theme) => `${theme.palette.background.primaryV3} !important`,
  },

  textField: {
    '& .MuiOutlinedInput-root': {
      border: '1px solid',
      borderRadius: '6px',
      borderColor: (theme) => theme.palette.background.ternary,
      backgroundColor: (theme) => theme.palette.background.secondary,
      '&:hover': {
        borderColor: (theme) => theme.palette.background.activeBorder,
      },
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: (theme) => theme.palette.text.placeholder,
    },
  },
  createBtn: {
    maxWidth: 350,
    boxShadow: 'none',
    color: (theme) => theme.palette.text.primary,
  },
};

export const RenderGateModal = ({
  open,
  mode,
  onClose,
  userPlanData,
  agentPlanData,
  push,
}: RenderGateModalProps) => {
  const theme = useTheme();
  const graySurface = alpha(theme.palette.grey[900], 0.96);
  const graySurfaceElevated = alpha(theme.palette.grey[800], 0.92);
  const graySurfaceMuted = alpha(theme.palette.grey[100], 0.05);
  const graySurfaceSoft = alpha(theme.palette.grey[100], 0.08);
  const grayBorder = alpha(theme.palette.grey[100], 0.12);
  const grayBorderSoft = alpha(theme.palette.grey[100], 0.08);
  const grayBorderHover = alpha(theme.palette.grey[100], 0.18);
  const grayTextMuted = alpha(theme.palette.grey[100], 0.82);
  const grayTextSubtle = alpha(theme.palette.grey[100], 0.52);
  const grayTextDim = alpha(theme.palette.grey[100], 0.34);
  const grayTextFaint = alpha(theme.palette.grey[100], 0.22);
  const showTrialColumn = userPlanData === 'freeTrialAccess' || agentPlanData === 'agentTrialPlan';
  const showPaidColumn = agentPlanData !== 'agentTrialPlan';
  const showAgentColumn =
    (userPlanData === 'activePlan' && agentPlanData !== 'agentPlan') ||
    agentPlanData === 'agentTrialPlan';
  const showStandardComparisonRows = userPlanData !== 'activePlan' && agentPlanData !== 'agentPlan';

  const FeatureTableRow: React.FC<{
    name: React.ReactNode;
    trialValue?: React.ReactNode;
    paidValue: React.ReactNode;
    agentValue?: React.ReactNode;
    locked?: boolean;
    includeTrialColumn?: boolean;
  }> = ({ name, trialValue, paidValue, agentValue, locked, includeTrialColumn = true }) => (
    <tr>
      <td>{name}</td>
      {includeTrialColumn && showTrialColumn && (
        <td
          style={{
            color: locked ? grayTextFaint : `${theme.palette.grey[100]} !important`,
            fontWeight: 600,
            textAlign: 'left',
          }}
        >
          {trialValue}
        </td>
      )}
      {showPaidColumn && (
        <td
          style={{
            color: `${theme.palette.text.primary} !important`,
            background: `${theme.palette.background.selectedCardBorder}40`,
            fontWeight: 600,
            textAlign: 'left',
          }}
        >
          {paidValue}
        </td>
      )}
      {showAgentColumn && (
        <td
          style={{
            color: `${theme.palette.text.primary} !important`,
            background: `${theme.palette.background.selectedCardBorder}40`,
            fontWeight: 600,
            textAlign: 'left',
          }}
        >
          {agentValue}
        </td>
      )}
    </tr>
  );

  const PlanFeatureValue: React.FC<{ text: string }> = ({ text }) => (
    <Box
      sx={{
        flex: 1,
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'row',
        gap: 0.5,
      }}
    >
      <span>✓</span>
      <span>{text}</span>
    </Box>
  );

  const renderPlanFeatureValue = (text: string) => <PlanFeatureValue text={text} />;

  return (
    <CustomDialogContainer
      PaperProps={{
        sx: {
          maxWidth: '750px !important',
          border: (theme) => `0.5px solid ${theme.palette.background.borderV2}`,
          backgroundColor: (theme) => `${theme.palette.background.primaryV3} !important`,
        },
      }}
      sx={{
        '& .MuiPaper-root.MuiDialog-paper': {
          backgroundColor: (theme) => `${theme.palette.background.primaryV3} !important`,
        },
      }}
      open={open}
      maxWidth="sm"
    >
      <CustomDialogTitle
        sx={styles.title}
        title={mode === 'processing' ? 'Processing...' : 'Premium Feature '}
        onClose={onClose}
      />
      <DialogContent dividers sx={styles.dialogContent}>
        <Box
          sx={{
            maxWidth: 650,
            borderRadius: 2,
            mx: 'auto',
            textAlign: 'center',
          }}
        >
          {mode === 'processing' && (
            <>
              <CircularProgress />
            </>
          )}

          {(mode === 'upgrade' || mode === 'noPlan') && (
            <>
              <Typography sx={{ mt: 2, mb: 2 }}>
                {mode === 'noPlan'
                  ? "You currently don't have any active plan"
                  : "You're currently on a Free Trial."}
                <br />
                This feature is available on paid plans only.
              </Typography>

              <Box
                sx={{
                  position: 'relative',
                  borderRadius: '16px',
                  border: `1px solid ${grayBorder}`,
                  background: `linear-gradient(180deg, ${graySurface}, ${graySurfaceElevated})`,
                  p: { xs: 1, sm: 1.5, md: 2 },
                  textAlign: 'left',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: 0.75, sm: 1 },
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      borderRadius: '14px',
                      border: `1px solid ${grayBorderSoft}`,
                      background: graySurfaceMuted,
                      p: { xs: 0.75, sm: 1 },
                    }}
                  >
                    <Box
                      component="table"
                      sx={{
                        width: '100%',
                        minWidth: '100%',
                        tableLayout: 'fixed',
                        borderCollapse: 'collapse',
                        fontSize: { xs: '0.66rem', sm: '0.8rem' },
                        '& th': {
                          padding: { xs: '3px 4px', sm: '4px 6px' },
                          fontSize: { xs: '0.63rem', sm: '0.85rem' },
                          color: grayTextDim,
                          fontWeight: 500,
                          textAlign: 'left',
                          borderBottom: `0.5px solid ${grayBorderSoft}`,
                        },
                        '& th:not(:first-of-type)': {
                          textAlign: 'left',
                        },
                        '& td': {
                          padding: { xs: '3px 4px', sm: '5px 6px' },
                          borderBottom: `0.5px solid ${grayBorderSoft}`,
                          color: grayTextMuted,
                          lineHeight: 1.25,
                        },
                        '& td:not(:first-of-type)': {
                          textAlign: 'left',
                        },
                        '& th:first-of-type, & td:first-of-type': {
                          width: '36%',
                          whiteSpace: 'normal',
                          overflowWrap: 'anywhere',
                        },
                        '& th:not(:first-of-type), & td:not(:first-of-type)': {
                          width: '64%',
                        },
                      }}
                    >
                      <Box component="thead">
                        <Box component="tr">
                          <Box
                            component="th"
                            sx={{ color: `${theme.palette.grey[100]} !important`, opacity: 0.95 }}
                          >
                            Feature
                          </Box>
                          {!showAgentColumn && (
                            <Box
                              component="th"
                              sx={{
                                textAlign: 'left',
                                color: `${theme.palette.grey[100]} !important`,
                                background: theme.palette.background.selectedCardBorder,
                                opacity: 0.95,
                              }}
                            >
                              With Fully Paid Plan
                            </Box>
                          )}
                          {showAgentColumn && (
                            <Box
                              component="th"
                              sx={{
                                textAlign: 'left',
                                color: `${theme.palette.text.primary} !important`,
                                background: theme.palette.background.selectedCardBorder,
                                opacity: 0.95,
                              }}
                            >
                              With A Fully Paid Everything Agent Plan
                            </Box>
                          )}
                        </Box>
                      </Box>
                      <Box component="tbody">
                        {showAgentColumn && (
                          <FeatureTableRow
                            includeTrialColumn={false}
                            name="Bulk UGC Generation"
                            paidValue={''}
                            agentValue={renderPlanFeatureValue(
                              'Generate 20+ UGC videos from one product upload'
                            )}
                            locked
                          />
                        )}

                        {showAgentColumn && (
                          <FeatureTableRow
                            includeTrialColumn={false}
                            name="Batch Video Creation"
                            paidValue={''}
                            agentValue={renderPlanFeatureValue(
                              'Create 50 short-form clips in one batch'
                            )}
                          />
                        )}

                        {showAgentColumn && (
                          <FeatureTableRow
                            includeTrialColumn={false}
                            name="Bulk Image Creation"
                            paidValue={''}
                            agentValue={renderPlanFeatureValue('Generate 100+ image ads instantly')}
                          />
                        )}

                        {showAgentColumn && (
                          <FeatureTableRow
                            includeTrialColumn={false}
                            name="Background Queue"
                            paidValue={'-'}
                            agentValue={renderPlanFeatureValue(
                              'Queue jobs and run them in the background'
                            )}
                          />
                        )}

                        {showAgentColumn && (
                          <FeatureTableRow
                            includeTrialColumn={false}
                            name="Parallel Generations"
                            paidValue={'-'}
                            agentValue={renderPlanFeatureValue(
                              'Run multiple image and video jobs at once'
                            )}
                          />
                        )}

                        {showAgentColumn && (
                          <FeatureTableRow
                            includeTrialColumn={false}
                            name="Social Media Content"
                            paidValue={'-'}
                            agentValue={renderPlanFeatureValue(
                              'Bulk-create Social Media content and ad creatives faster'
                            )}
                          />
                        )}

                        {showAgentColumn && (
                          <FeatureTableRow
                            includeTrialColumn={false}
                            name="Agnostic Agent"
                            paidValue={'-'}
                            agentValue={renderPlanFeatureValue(
                              'All models work together agentically in the background'
                            )}
                          />
                        )}

                        {showAgentColumn && (
                          <FeatureTableRow
                            includeTrialColumn={false}
                            name="MCP Server"
                            paidValue={'-'}
                            agentValue={renderPlanFeatureValue(
                              'Connect with any MCP-compatible AI agent using our agent MCP'
                            )}
                          />
                        )}

                        {showStandardComparisonRows && (
                          <FeatureTableRow
                            includeTrialColumn={false}
                            name={'Premium Models'}
                            paidValue={renderPlanFeatureValue('All Models Unlocked')}
                            agentValue={renderPlanFeatureValue('All Models Unlocked')}
                          />
                        )}

                        {showStandardComparisonRows && (
                          <FeatureTableRow
                            includeTrialColumn={false}
                            name={'Models Included'}
                            paidValue={
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  gap: { xs: 0.25, sm: 0.75 },
                                  flexWrap: 'wrap',
                                }}
                              >
                                {['Seedance 2.5', 'Kling 3', 'Nano Banana Pro', 'GPT Image 2'].map(
                                  (model) => (
                                    <Box
                                      key={model}
                                      component="span"
                                      sx={{
                                        display: 'block',
                                        opacity: 0.85,
                                        fontSize: { xs: '9px', sm: '12px' },
                                        whiteSpace: 'nowrap',
                                        background: graySurfaceSoft,
                                        padding: '5px',
                                        borderRadius: '6px',
                                      }}
                                    >
                                      {model}
                                    </Box>
                                  )
                                )}
                              </Box>
                            }
                            agentValue={
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  gap: { xs: 0.25, sm: 0.75 },
                                  flexWrap: 'wrap',
                                }}
                              >
                                {['Seedance 2.5', 'Kling 3', 'Nano Banana Pro', 'GPT Image 2'].map(
                                  (model) => (
                                    <Box
                                      key={model}
                                      component="span"
                                      sx={{
                                        display: 'block',
                                        opacity: 0.85,
                                        fontSize: { xs: '9px', sm: '12px' },
                                        whiteSpace: 'nowrap',
                                        background: graySurfaceSoft,
                                        padding: '5px',
                                        borderRadius: '6px',
                                      }}
                                    >
                                      {model}
                                    </Box>
                                  )
                                )}
                              </Box>
                            }
                          />
                        )}

                        {showStandardComparisonRows && (
                          <FeatureTableRow
                            includeTrialColumn={false}
                            name="Commercial Export License"
                            paidValue={renderPlanFeatureValue(
                              'Use for Ads, UGC & Commercial Projects'
                            )}
                            agentValue={renderPlanFeatureValue(
                              'Use for Ads, UGC & Commercial Projects'
                            )}
                            locked
                          />
                        )}

                        {showStandardComparisonRows && (
                          <FeatureTableRow
                            includeTrialColumn={false}
                            name="Video Quality & Duration"
                            paidValue={renderPlanFeatureValue(
                              'Generate longer, higher-quality videos and images'
                            )}
                            agentValue={renderPlanFeatureValue(
                              'Generate longer, higher-quality videos and images'
                            )}
                            locked
                          />
                        )}

                        {showStandardComparisonRows && (
                          <FeatureTableRow
                            includeTrialColumn={false}
                            name="Processing Speed"
                            paidValue={renderPlanFeatureValue(
                              'Skip the wait with priority processing speed'
                            )}
                            agentValue={renderPlanFeatureValue(
                              'Skip the wait with priority processing speed'
                            )}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* <Typography sx={{ mt: 1 }}>Upgrade now to finish rendering your result.</Typography> */}

              <Button
                fullWidth
                sx={{ ...styles.createBtn, mt: 3, mb: 3 }}
                variant="contained"
                onClick={() => {
                  if (userPlanData === 'noActivePlan' && agentPlanData === 'noAgentPlan') {
                    push(`/subscription?from=upgrade`);
                  } else if (agentPlanData === 'agentTrialPlan') {
                    push(`/subscription?name=Everything Agent&from=upgrade`);
                  } else {
                    push(`/subscription?name=All Model Access Plan&from=upgrade`);
                  }
                }}
              >
                Upgrade to Finish Rendering
              </Button>

              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                Your prompt is saved — you won’t lose your work.
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>
    </CustomDialogContainer>
  );
};
